// Persistent device diagnostics log — the flight recorder for venue issues.
//
// Every connectivity transition, sync failure, print failure, submit, and
// outbox event lands here, survives restarts (IndexedDB), keeps 48 hours,
// and is pushed to the DinePlatform Hub every minute (hub.ts) so the full
// multi-device story is readable remotely from the Pi at any time.

import { kvGet, kvSet } from "../data/idb";

export interface LogEntry {
  t: number;
  ev: string;
  d?: string;
  /** Actor: the signed-in staff member at the time. */
  u?: string;
}

const KEY = "devlog";
const MAX_AGE_MS = 48 * 3600_000;
const MAX_ENTRIES = 5000;

let buf: LogEntry[] = [];
let cache: LogEntry[] | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing: Promise<void> = Promise.resolve();
let actor = "";

/** StaffContext keeps this current — every entry records who did it. */
export function setLogActor(name: string | undefined | null): void {
  actor = name || "";
}

/** Record one event. `detail` is stringified and truncated — log freely. */
export function logEvent(ev: string, detail?: unknown): void {
  const d =
    detail === undefined
      ? undefined
      : String(typeof detail === "string" ? detail : JSON.stringify(detail)).slice(0, 1000);
  buf.push({ t: Date.now(), ev, ...(d !== undefined ? { d } : {}), ...(actor ? { u: actor } : {}) });
  if (!flushTimer) flushTimer = setTimeout(() => void flushLog(), 30_000);
}

async function flushLog(): Promise<void> {
  flushTimer = null;
  flushing = flushing.catch(() => {}).then(async () => {
    if (buf.length === 0) return;
    const taking = buf;
    buf = [];
    const cutoff = Date.now() - MAX_AGE_MS;
    const q = (cache ?? (await kvGet<LogEntry[]>(KEY)) ?? []).filter((e) => e.t >= cutoff);
    q.push(...taking);
    cache = q.slice(-MAX_ENTRIES);
    await kvSet(KEY, cache);
  });
  await flushing;
}

/** Entries newer than `since` (for the hub pusher). Reads the persisted log
 *  plus the in-memory tail — it does NOT force a disk write (the 30 s timer
 *  and page-hide do that), so pushing logs to the hub costs no flash writes. */
export async function logsSince(since: number): Promise<LogEntry[]> {
  const q = [...(cache ?? (await kvGet<LogEntry[]>(KEY)) ?? []), ...buf];
  return q.filter((e) => e.t > since);
}
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (document.hidden) void flushLog(); });
  window.addEventListener("pagehide", () => void flushLog());
}
